import { useEffect, useState } from 'react';
import { Form, FormikProvider, useFormikContext } from 'formik';
import { toFormikValidate, toFormikValidationSchema } from 'zod-formik-adapter';
import Spacer from '@antimatterhq/ui/components/spacer';
import { PresetHeading } from '../../_partials/PresetHeading';
import { GoldModifyTables } from './partials/GoldModifyTables';
import { GoldAddTables } from './partials/GoldAddTables';
import { useFormikWithAsyncValidation } from '@/hooks/form/useFormikWithAsyncValidation';
import { getGoldInitialValues, setGoldFormValues } from '@/lib/datasource';
import {
  GoldDataSourceSchema,
  GoldDataSourceSchemaValues,
} from '@/zod/schemas/dataSource/Gold';
import { DataSourceNavigationButtons } from '@/components/button/dataSourceNavigation/DataSourceNavigationButtons';
import {
  DatasourceSubmitType,
  useConfigDataSource,
} from '@/contexts/ConfigDataSourceContext';
import { TwoColumnLayout } from '@/components/container';
import { TableOfContentsProvider } from '@/contexts/TableOfContentContext';
import { TableOfContents } from '@/components/tableOfContents/TableOfContents';
import { useDebounceValidation } from '@/hooks/formik/useDebounceValidation';
import { AsyncValidationProvider } from '@/contexts/AsyncValidationContext';

export type GoldFormProps = {
  submitAction: DatasourceSubmitType;
  setSubmitAction: (action: DatasourceSubmitType) => void;
  submitDisabled?: boolean;
};

const GoldForm = ({
  submitAction,
  setSubmitAction,
  submitDisabled = false,
}: GoldFormProps) => {
  const { handlePreviousStep, isSaving, setFormik } = useConfigDataSource();
  const { values, handleSubmit, dirty } =
    useFormikContext<GoldDataSourceSchemaValues>();

  useEffect(() => {
    setFormik({ handleSubmit, dirty });
  }, [handleSubmit, dirty, setFormik]);

  return (
    <Form data-testid="gold-form">
      <DataSourceNavigationButtons
        disabled={isSaving || submitDisabled}
        goToPreviousStep={handlePreviousStep}
        handlePreviousStep={() => setSubmitAction('previous')}
        handleNextStep={() => setSubmitAction('next')}
        handleReview={() => setSubmitAction('review')}
        loadingNext={submitAction === 'next' && isSaving}
        loadingReview={submitAction === 'review' && isSaving}
        data-testid="gold-top-navigation-buttons"
      />

      <Spacer size="lg" />

      <GoldModifyTables />

      <Spacer size="lg" />

      <GoldAddTables />

      <Spacer size="2xl" />

      {(!!values.addTables.length || !!values.modifyTables.length) && (
        <DataSourceNavigationButtons
          disabled={isSaving || submitDisabled}
          goToPreviousStep={handlePreviousStep}
          handlePreviousStep={() => setSubmitAction('previous')}
          handleNextStep={() => setSubmitAction('next')}
          handleReview={() => setSubmitAction('review')}
          loadingNext={submitAction === 'next' && isSaving}
          loadingReview={submitAction === 'review' && isSaving}
          data-testid="gold-bottom-navigation-buttons"
        />
      )}
    </Form>
  );
};

function DataSourceGoldContent() {
  const { dataSource, handleNextStep, handleReview, save, handlePreviousStep } =
    useConfigDataSource();

  const [submitAction, setSubmitAction] = useState<DatasourceSubmitType>('');
  const formik = useFormikWithAsyncValidation({
    initialValues: getGoldInitialValues(dataSource),
    validate: toFormikValidate(GoldDataSourceSchema),
    validationSchema: toFormikValidationSchema(GoldDataSourceSchema),
    validateOnChange: false,
    onSubmit: async (newValues) => {
      // update state
      const newDataSource = setGoldFormValues(dataSource, newValues);
      const result = await save(newDataSource);

      if (!result) {
        return;
      }

      // go to next step
      if (submitAction === 'next') {
        handleNextStep();
      } else if (submitAction === 'review') {
        handleReview();
      } else if (submitAction === 'previous') {
        handlePreviousStep();
      }
    },
  });
  useDebounceValidation(formik);

  return (
    <TableOfContentsProvider>
      <TwoColumnLayout.Root data-testid="gold-layout-root">
        <TwoColumnLayout.LeftColumn>
          <PresetHeading />
          <FormikProvider value={formik}>
            <GoldForm
              submitAction={submitAction}
              setSubmitAction={setSubmitAction}
              submitDisabled={formik.submitDisabled}
            />
          </FormikProvider>
        </TwoColumnLayout.LeftColumn>
        <TwoColumnLayout.RightColumn>
          <TableOfContents data-testid="gold-table-of-contents" />
        </TwoColumnLayout.RightColumn>
      </TwoColumnLayout.Root>
    </TableOfContentsProvider>
  );
}

export default function DataSourceGold() {
  return (
    <AsyncValidationProvider>
      <DataSourceGoldContent />
    </AsyncValidationProvider>
  );
}
